<template>
  <div
    id="theme"
    :class="{
      'theme--default': !(theme === 'dark' && animationEnded),
      'theme--dark': theme === 'dark' && animationEnded,
    }"
  >
    <!-- Teleport Receiver -->
    <div id="modal" />
    <div id="app">
      <AlreadyRunningScreen v-if="alreadyRunning" />
      <Login
        v-else-if="authenticate"
        :deeplink="authenticate.deeplink"
        :uri="authenticate.uri"
      />
      <LoginProgress
        v-else-if="loginProgress"
        :notification="notification"
        :state="loginProgress"
      />
      <UpgradeRequired
        v-else-if="requiredVersion"
        :notification="notification"
        :required-version="requiredVersion"
      />
      <div v-else-if="!animationEnded" class="logo">
        <BaseViewLoader />
        <Notification v-if="notification" :notification="notification" />
      </div>
      <Suspense
        v-else-if="
          controller &&
          notificationsHook &&
          hasCompletedOnboarding !== undefined
        "
      >
        <Routine
          ref="routineEl"
          :controller="controller"
          :notifications-hook="notificationsHook"
          :has-completed-onboarding-previously="hasCompletedOnboarding"
        />
      </Suspense>
    </div>

    <Featurebase
      v-if="externalServicesEnabled && controller && hasCompletedOnboarding"
      organization="routine"
      theme="auto"
      :email="profile.user.email"
      :controller="controller"
    />
  </div>
</template>

<script lang="ts">
import { useHead } from "@unhead/vue";
import { syncRef } from "@vueuse/core";
import { Mutex } from "async-mutex";
import {
  computed,
  defineComponent,
  onErrorCaptured,
  Ref,
  ref,
  shallowRef,
  watch,
} from "vue";

import { metrics } from "@/metrics";
import { sentry } from "@/utils/sentry";

import "vue-final-modal/style.css";

import { IpcRendererEvent } from "electron";
import {
  isNavigationFailure,
  NavigationGuardNext,
  RouteLocation,
  useRoute,
} from "vue-router";

import controller, { ReleasableController } from "@ctrl/controller";
import type {
  Controller,
  Login as LoginSchema,
  LoginStatus,
  Notification as NotificationSchema,
} from "@ctrl/controller-rpc";
import type { Hook } from "@ctrl/hook";

import AlreadyRunningScreen from "@/components/AlreadyRunning.vue";
import BaseViewLoader from "@/components/base/BaseViewLoader.vue";
import Featurebase from "@/components/feedback/Featurebase.vue";
import * as inject from "@/components/inject";
import Login from "@/components/Login.vue";
import LoginProgress, {
  LoginProgressState,
} from "@/components/LoginProgress.vue";
import Notification from "@/components/notification/Notification.vue";
import Routine from "@/components/Routine.vue";
import UpgradeRequired from "@/components/UpgradeRequired.vue";
import useDirection from "@/composables/useDirection";
import {
  EMPTY_PROFILE,
  useProfile,
  type UseProfile,
} from "@/composables/useProfile";
import useTheme from "@/composables/useTheme";
import { join } from "@/hotkeys/join";
import { logger as appLogger } from "@/logger/app";
import { logger as rendererLogger } from "@/logger/renderer";
import { paramsOfUrl } from "@/login";
import { useReleaser, withRelease } from "@/ref";
import { browser } from "@/services/browser";
import { installE2eShims } from "@/tests/e2e";
import { openUpgradeBlockedModal, parseUpgradeBlockedEvent } from "@/upgrade";
import { isExtensionError } from "@/utils/error";
import { externalServicesEnabled } from "@/utils/external-services";
import { in_electron } from "@/utils/in";
import { decode as decodeJwt } from "@/utils/jwt";
import { locale } from "@/utils/locale";
import { navigateTo } from "@/utils/navigate";
import { spellcheck } from "@/utils/spellcheck";
import {
  activeReactivityTrace,
  installReactivityTrace,
} from "@/utils/trace/reactivity";
import { property } from "@/utils/unknown";

const { makeControllerElectronIPCClient, makeWebworkerController, setupLogs } =
  controller;

class AlreadyRunning extends Error {}
globalThis.AlreadyRunning = AlreadyRunning;

class Authenticate extends Error {
  uri: string;
  deeplink: boolean;
  scopes: Array<string> | null;
  constructor(uri: string, scopes?: Array<string>) {
    super("authentication required");
    this.uri = uri;
    this.deeplink = in_electron;
    this.scopes = scopes || null;
  }
}
globalThis.Authenticate = Authenticate;

class UpgradeRequiredError extends Error {
  version: string;
  constructor(version: string) {
    super("upgrade required");
    this.version = version;
  }
}

export default defineComponent({
  name: "App",
  components: {
    AlreadyRunningScreen,
    BaseViewLoader,
    Featurebase,
    Login,
    Notification,
    Routine,
    LoginProgress,
    UpgradeRequired,
  },
  setup() {
    setupLogs();
    const router = globalThis.router;
    const route = useRoute();
    useHead({
      title: computed(() => route.name?.toString() ?? ""),
    });
    const routineEl = ref<InstanceType<typeof Routine>>();
    const notificationsHook = shallowRef<Hook<NotificationSchema>>();
    const controller = shallowRef<ReleasableController | null>(null);

    // Get the app version from build-time constant; otherwise we would need to
    // wait for the controller to initialize which requires the user to log in.
    metrics.segment.init(__APP_VERSION__);
    metrics.segment.setup.router(router);
    metrics.segment.setup.ipc();

    // Initialize spellcheck
    spellcheck.renderer.initialize(locale.get());

    // Retrieve the preselected plan and billing interval from the URL query
    // parameters.
    //
    // This allows the user launching Routine from /pricing to have
    // the proper plan preselected in the onboarding flow.
    //
    // Save the values in the store for the onboarding plan step to use later.
    // Watched (like the extension capture below) because the router may
    // resolve the query after first paint.
    watch(
      () => route.query["plan"],
      (plan) => {
        if (typeof plan !== "string" || plan.length === 0) return;
        // The interval only makes sense alongside a plan; default to yearly.
        const interval = route.query["interval"] === "month" ? "month" : "year";
        browser.store.set("temporary:preselectedPlan", plan);
        browser.store.set("temporary:preselectedInterval", interval);
      },
      { immediate: true }
    );

    // Marketplace extension from deeplink / web URL — persist whenever it appears
    // on the route (router may resolve after first paint; client navigations and
    // OAuth returns often omit ?extension=, so capture early while it’s present).
    watch(
      () => route.query.extension,
      (ext) => {
        if (typeof ext === "string" && ext.length > 0) {
          browser.store.set("temporary:extensionId", ext);
        }
      },
      { immediate: true }
    );

    // Fatal errors handling
    const captureFatalError = async (
      controller: Controller | null,
      error: unknown,
      mechanism: string,
      crash: boolean
    ) => {
      const fatalMessage = (message: string) => {
        sentry.captureFatalMessage(message);
        if (crash)
          if (controller) controller.err(message || "Unknown error");
          else {
            // eslint-disable-next-line no-console
            console.error("fatal error", error);
            notification.value = {
              id: "fatal-error",
              message: [message],
              message_str: message,
              time: new Date(),
              type: "error",
            };
          }
        // eslint-disable-next-line no-console
        else console.error("fatal error", error);
      };
      // Trying to silence errors from FrontApp.  You cannot scope
      // this event listener to a script HTMLElement.  After
      // looking at the code from front app, they dynamically load
      // their SDK. As of today request errors yeild a rejected
      // promise with an Error object.
      if (
        typeof error === "object" &&
        error !== null &&
        "message" in error &&
        typeof error.message === "string" &&
        error.message.startsWith("Request failed with status code ")
      ) {
        // eslint-disable-next-line no-console
        console.warn(`promise error from FrontApp: ${error}`);
      } else {
        if (error instanceof Authenticate) {
          // FIXME: probably a bit blunt
          window.location.reload();
        } else if (error instanceof Error) {
          if (crash)
            if (controller) controller.err(error.message);
            else {
              // eslint-disable-next-line no-console
              console.error("fatal error", error);
              notification.value = {
                id: "fatal-error",
                message: [error.message],
                message_str: error.message,
                time: new Date(),
                type: "error",
              };
            }
          // eslint-disable-next-line no-console
          else console.error("fatal error", error);
          await sentry.captureFatalError(error, mechanism);
        } else if (typeof error === "string") return fatalMessage(error);
        else if (error instanceof String) fatalMessage(error.valueOf());
        else if (
          error instanceof Object &&
          "target" in error &&
          "isTrusted" in error
        ) {
          const target = error.target;
          const src =
            typeof target === "object" && target !== null && "src" in target
              ? String(target.src)
              : undefined;
          fatalMessage(`error loading script ${src}`);
        } else fatalMessage(JSON.stringify(error));
      }
    };
    onErrorCaptured((error: unknown) => {
      const fatal =
        typeof error === "object" &&
        error !== null &&
        "name" in error &&
        error.name === "ControllerFatal";
      captureFatalError(controller.value, error, "onerror", fatal);
      return false;
    });
    // Suppress default console log
    window.onerror = () => true;
    window.addEventListener("error", function (event) {
      // Prevent console logging
      event.preventDefault();
      // Prevent handling by sentry
      event.stopImmediatePropagation();
      // Extensions share the page context: their own uncaught errors reach
      // this handler but are not app failures (see FRONTEND-FHE).
      if (isExtensionError(event.error)) {
        window.console.warn("Ignoring browser extension error:", event.error);
        return;
      }
      const fatal = event.error?.name === "ControllerFatal";
      captureFatalError(
        controller.value,
        event.error || event.message,
        "onerror",
        fatal
      );
    });
    window.addEventListener("unhandledrejection", function (event) {
      // Prevent console logging
      event.preventDefault();
      // Prevent handling by sentry
      event.stopImmediatePropagation();
      // Failures from `electron.shell.openExternal` (e.g. unsupported URL
      // protocol) reject with a `Failed to open URL` Error. They are user-facing
      // and should never tear down the app; surface them as a non-fatal toast
      // and keep going. `openExternalUrl` already handles these where it is
      // called directly, this is the safety net for any other path.
      const reason = event.reason;
      const message =
        typeof reason === "object" && reason !== null && "message" in reason
          ? String(property(reason, "message"))
          : "";
      if (message.startsWith("Failed to open URL")) {
        window.console.warn("Ignoring non-fatal openExternal failure:", reason);
        controller.value?.notifications.notify("Couldn't open this link");
        return;
      }
      if (message.includes("segment.io") || message.includes("segment.com")) {
        window.console.warn(
          "Ignoring non-fatal Segment analytics failure:",
          reason
        );
        return;
      }
      // A rejection thrown entirely inside a browser extension's injected
      // script (e.g. a crypto wallet's inpage.js) is not an app failure: it
      // must neither show the crash screen nor be reported (FRONTEND-FHE).
      if (isExtensionError(reason)) {
        window.console.warn("Ignoring browser extension error:", reason);
        return;
      }
      captureFatalError(
        controller.value,
        event.reason,
        "onunhandledrejection",
        true
      );
    });

    router.beforeEach(
      async (
        _to: RouteLocation,
        _from: RouteLocation,
        next: NavigationGuardNext
      ) => {
        next();
      }
    );

    // Injections
    inject.inDashboard(false);
    inject.isInputFocused(ref(false));
    inject.isModalShown(ref(false));
    inject.logout(async () => {
      await controller.value?.auth.logout();
      // We refresh the page just after which completely nukes everything
      // anyway, but start wiring things so that we can leave this model and
      // cleanup after ourselves at some point.
      controller.value?.release();
      globalThis.controller = null;
      window.location.reload();
      window.location.replace("/");
    });
    inject.router(router);

    const logger = rendererLogger.renderer.wrapper();
    inject.logger(logger);

    // The controller and profile acquired in login() are add()ed to it,
    // including acquisitions from repeated logins.
    const releaser = useReleaser();

    const { themePreference, theme, release: releaseTheme } = useTheme();
    releaser.defer(releaseTheme);
    inject.themePreference(themePreference);
    inject.theme(theme);

    const {
      directionPreference,
      direction,
      release: releaseDirection,
    } = useDirection();
    releaser.defer(releaseDirection);
    inject.directionPreference(directionPreference);
    inject.direction(direction);

    const newVersion = ref<string>();
    inject.newVersion(newVersion);

    // Provide a stable injected profile ref. `useProfile()` resolves later
    // (after login), so we inject a placeholder now and sync it once the real
    // profile arrives. The placeholder is never observable: every consumer
    // lives under <Routine>, which only mounts once login() has synced the
    // real profile (consumers may pass the personal workspace id to RPCs at
    // setup, and the controller client rejects the placeholder's empty id
    // with a fatal error).
    const profileValue: Ref<UseProfile> = ref({ ...EMPTY_PROFILE });
    let currentProfile: { release: () => unknown } | null = null;
    const profile = withRelease(profileValue, () => currentProfile?.release());
    inject.profile(profile);

    // Data
    const alreadyRunning = ref(false);
    const authenticate = ref<{
      deeplink: boolean;
      scopes: Array<string> | null;
      uri: string;
    } | null>(null);
    const hasCompletedOnboarding = ref<boolean>();
    const animationEnded = ref(false);
    const loginProgress = ref<LoginProgressState>();
    const requiredVersion = ref<string>();

    watch(
      hasCompletedOnboarding,
      (v) => {
        if (v !== undefined) animationEnded.value = true;
      },
      { immediate: true }
    );
    const notification = ref<NotificationSchema | null>(null);

    const loginMutex = new Mutex();

    function applyLoginProgress(status: LoginStatus): boolean {
      // Not a terminal status: login is retrying, and resumes on the step it
      // last reported.
      if (status.status === "offline") {
        loginProgress.value = { status: "offline" };
        return true;
      }
      if (status.status !== "in_progress") return false;
      // A step-less progress report keeps the step being displayed, so that it
      // still takes us out of the offline state.
      const step =
        status.step ??
        (loginProgress.value?.status === "in_progress"
          ? loginProgress.value.step
          : null);
      if (step != null) loginProgress.value = { status: "in_progress", step };
      if (status.progress !== undefined) {
        // eslint-disable-next-line no-console
        console.log(`Login progress: ${status.progress}%`);
      }
      return true;
    }

    function clearLoginProgress() {
      loginProgress.value = undefined;
    }

    // Login function
    async function login(login: LoginSchema) {
      authenticate.value = null;
      try {
        const [rawCtrl, wasAccountCreated] = await (async () => {
          if (in_electron) {
            const { ipcRenderer: ipc } = require("electron");
            const logged_in = new Promise<LoginStatus>((resolve) => {
              let loginListener:
                | ((event: IpcRendererEvent, result: LoginStatus) => void)
                | null = null;

              loginListener = (
                _event: IpcRendererEvent,
                result: LoginStatus
              ) => {
                if (applyLoginProgress(result)) return;
                clearLoginProgress();
                if (loginListener) {
                  ipc.removeListener("controller-login", loginListener);
                }
                resolve(result);
              };
              ipc.on("controller-login", loginListener);
            });
            ipc.send("controller-login", login);
            const result: LoginStatus = await logged_in;
            // eslint-disable-next-line no-console
            console.log(`Login result: ${result.status}`, result);
            if (result.login) {
              if (result.status === "success") {
                const ctrl = releaser.add(
                  makeControllerElectronIPCClient("controller-main")
                );
                return [ctrl, result.accountCreated];
              } else {
                throw new Error("Login succeeded but status is not success");
              }
            } else if (result.status === "sign_in") {
              throw new Authenticate(result.url);
            } else if (result.status === "failure") {
              throw new Error(result.error);
            } else if (result.status === "upgrade_required") {
              throw new UpgradeRequiredError(result.version);
            } else {
              throw new Error("Unknown error starting the controller");
            }
          } else {
            try {
              const { controller, accountCreated } =
                await makeWebworkerController(login, applyLoginProgress);
              const client = controller.client("main");
              return [
                Object.assign(client, { release: () => undefined }),
                accountCreated,
              ];
            } finally {
              clearLoginProgress();
            }
          }
        })();
        // Opt-in reactivity tracing (VITE_REACTIVITY_TRACE=1, see
        // trace/reactivity.ts): wrap
        // before any RPC so login-time subscriptions (useProfile) are
        // traced too. Passthrough when disabled.
        const ctrl = installReactivityTrace(rawCtrl);
        globalThis.controller = ctrl;

        installE2eShims(ctrl, logger);

        // Initialize the profile and overwrite the ref that was
        // already been provided.
        const _profile = await useProfile(ctrl);
        activeReactivityTrace()?.traceComposite("useProfile", _profile);
        releaser.add(_profile);
        currentProfile = _profile;
        syncRef(_profile, profileValue, { direction: "ltr" });

        // Identify the logged in user
        //
        // At sign up, this is the first IDENTIFY call made, passing wasAccountCreated=true
        // to send all the flags passed by the website (utm, referral, etc.)
        metrics.segment.identify(
          _profile.value.user.accountId,
          {},
          wasAccountCreated
        );

        // Defer welcoming the user to the new organization until
        // the user has logged in.
        if (login.intention === "join_organization") {
          const orgId = decodeJwt(login.invitation_token)?.org;
          if (orgId) {
            const pending = {
              type: "organization",
              id: orgId,
            };
            browser.store.set("pending", pending, {
              format: "json",
            });
          }
        }

        const forceOnboarding = browser.store.getRaw(
          "forceOnboarding",
          undefined
        );
        if (forceOnboarding === "true") {
          // Force onboarding and clear the flag
          browser.store.unset("forceOnboarding");
          hasCompletedOnboarding.value = false;
        } else if (!wasAccountCreated) {
          hasCompletedOnboarding.value = true;
        } else {
          hasCompletedOnboarding.value =
            (await ctrl.flag.get("hasCompletedOnboarding")) || false;
        }
        const version = await ctrl.version();
        window.console.log(`version: ${version}`);
        const uid = await ctrl.auth.uid();
        window.console.log(`unique id: ${uid}`);
        const os = await ctrl.os();
        window.console.log(`os: ${os}`);
        const dist = await ctrl.distribution();
        window.console.log(`dist: ${dist}`);
        const hostname = await ctrl.hostname();
        window.console.log(`hostname: ${hostname}`);
        const dev = await ctrl.dev();
        const authProfile = await ctrl.auth.profile();
        authProfile.release();
        await sentry.initialize(dist, version, uid, authProfile.value, dev);

        notificationsHook.value = ctrl.notifications.notifications();
        controller.value = ctrl;
        // Renderer logs persist under Electron userData; no controller path needed.
        appLogger.app
          .initialize(_profile.value.user.accountId)
          .then(() => {
            logger.info("main", "Launching app");
          })
          .catch((err) => {
            window.console.error("Failed to initialize logger:", err);
          });

        if (in_electron) {
          const { ipcRenderer: ipc } = require("electron");
          ipc.on("new-version", (_event: IpcRendererEvent, name: string) => {
            window.console.log(`downloaded new app version: ${name}`);
            newVersion.value = name;
          });
          ipc.send("check-new-version");
        }
      } catch (error: unknown) {
        if (error instanceof AlreadyRunning) {
          alreadyRunning.value = true;
        } else if (error instanceof Authenticate) {
          const e = error;
          authenticate.value = {
            deeplink: e.deeplink,
            scopes: e.scopes,
            uri: e.uri,
          };
        } else if (error instanceof UpgradeRequiredError) {
          requiredVersion.value = error.version;
        } else {
          captureFatalError(null, error, "generic", true);
        }
      }
    }

    // IPC handlers
    if (in_electron) {
      // Set up the join hotkey.
      join.setup(controller);

      const { ipcRenderer: ipc } = require("electron");

      watch(theme, (theme) => ipc.send("theme-change", theme), {
        immediate: true,
      });

      ipc.on("login", (_event: IpcRendererEvent, params: LoginSchema) => {
        void loginMutex.runExclusive(async () => {
          const ctrl = controller.value;
          if (ctrl === null) {
            await login(params);
          } else if (params.intention === "join_organization") {
            const orgId = decodeJwt(params.invitation_token)?.org;
            try {
              await ctrl.auth.handleLoginIntent(params);
              if (typeof orgId === "string") {
                document.dispatchEvent(
                  new CustomEvent("pending-operation", {
                    detail: { type: "organization", id: orgId },
                  })
                );
              }
            } catch (error) {
              ctrl.notifications.err(
                error instanceof Error ? error.message : String(error)
              );
            }
          } else {
            ctrl.auth.handleLoginIntent(params);
          }
        });
      });
      ipc.on(
        "navigate",
        async (_event: IpcRendererEvent, url: RouteLocation) => {
          try {
            if (url?.name) {
              await navigateTo(url.name, router, {
                params: url.params,
                query: url.query,
              });
            } else {
              // eslint-disable-next-line no-console
              console.warn(
                `IPC navigate: No route name provided, falling back to router.push:`,
                url
              );
              await router.push(url);
            }
          } catch (e) {
            if (isNavigationFailure(e))
              // eslint-disable-next-line no-console
              console.info(`discard navigation: ${e}`);
            else throw e;
          }
        }
      );
      ipc.on(
        "capture-fatal-error",
        (
          _event: IpcRendererEvent,
          params: {
            error: unknown;
            mechanism: string;
            crash: boolean;
          }
        ) => {
          captureFatalError(
            controller.value,
            params.error,
            params.mechanism,
            params.crash
          );
        }
      );
      ipc.on(
        "report-error",
        (
          _event: IpcRendererEvent,
          params: {
            error: unknown;
            mechanism: string;
          }
        ) => {
          // Handled elsewhere (caught, shown to the user, recovered): only
          // file a non-fatal Sentry report with the logs attached.
          const error =
            params.error instanceof Error
              ? params.error
              : new Error(String(params.error));
          // eslint-disable-next-line no-console
          console.error("reported error", error);
          void sentry.captureHandledError(error, params.mechanism);
        }
      );
      ipc.on(
        "mode",
        (_event: IpcRendererEvent, mode: "foreground" | "background") =>
          controller.value?.foreground(mode === "foreground")
      );
      ipc.on(
        "controller-crashed",
        (
          _event: IpcRendererEvent,
          { code, signal }: { code: number | null; signal: string | null }
        ) => {
          const message = `Controller exited unexpectedly (code=${code}, signal=${signal})`;
          notification.value = {
            id: "fatal-error",
            message: [message],
            message_str: message,
            time: new Date(),
            type: "error",
          };
        }
      );
      ipc.on(
        "upgrade-blocked",
        (_event: IpcRendererEvent, payload: unknown) => {
          // The app cannot update itself (read-only volume, blocked by an
          // antivirus, ...): explain the situation and how to fix it.
          const blocked = parseUpgradeBlockedEvent(payload);
          if (blocked) {
            void openUpgradeBlockedModal(blocked);
          }
        }
      );
    }

    // Initialize
    void loginMutex.runExclusive(() =>
      login(
        paramsOfUrl(
          new URL(window.location.href),
          metrics.rewardful.getReferralId()
        )
      )
    );

    return {
      alreadyRunning,
      animationEnded,
      authenticate,
      controller,
      externalServicesEnabled,
      hasCompletedOnboarding,
      loginProgress,
      notification,
      notificationsHook,
      route,
      routineEl,
      theme,
      profile,
      requiredVersion,
    };
  },
  mounted() {
    if (in_electron) {
      const { ipcRenderer: ipc } = require("electron");
      ipc.send("main-window-ready");
    }
  },
  unmounted() {
    if (in_electron) {
      const { ipcRenderer: ipc } = require("electron");
      ipc.removeAllListeners("mode");
      ipc.removeAllListeners("capture-fatal-error");
      ipc.removeAllListeners("report-error");
      ipc.removeAllListeners("controller-crashed");
      ipc.removeAllListeners("navigate");
      ipc.removeAllListeners("new-version");
      ipc.removeAllListeners("upgrade-blocked");
    }
  },
  methods: {
    handleDoubleClick() {
      if (in_electron) {
        const { ipcRenderer: ipc } = require("electron");
        ipc.invoke("dblclick-titlebar");
      }
    },
  },
});
</script>

<style lang="scss">
@use "styles/fonts.scss";
@use "styles/global.scss";
@use "styles/tippy.scss";
@use "styles/checkbox.scss";
@use "styles/select.scss";
@import "tippy.js/dist/tippy.css";
</style>
<style scoped lang="scss">
#theme {
  .logo {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
  }
}
</style>
